import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Device, DeviceKind, DeviceStatus } from '@iot-dashboard/shared';
import { KIND_LABELS, SENSOR_KINDS, emptyStatusCounts } from '@iot-dashboard/shared';
import { useDevices, useSites } from '../../api/hooks';
import { BatteryMeter } from '../../components/BatteryMeter';
import { DataTable, type Column } from '../../components/DataTable';
import { KindIcon } from '../../components/KindIcon';
import { Panel } from '../../components/Panel';
import { StatusDot } from '../../components/StatusDot';
import { StatusSummary } from '../../components/StatusSummary';
import { ErrorState, Loading } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';
import { formatLux, formatPercent, formatSignal, formatTemperature } from '../../lib/format';

export function readingSummary(d: Device): string {
  const m = d.metrics;
  switch (d.kind) {
    case 'environmental':
      return [formatTemperature(m.temperature), formatPercent(m.humidity), m.light !== undefined ? formatLux(m.light) : null, m.leakDetected ? 'LEAK' : null].filter(Boolean).join(' · ');
    case 'entry':
      return m.isOpened === undefined ? '—' : m.isOpened ? 'Open' : 'Closed';
    case 'motion':
      return m.motionDetected === undefined ? '—' : m.motionDetected ? 'Motion detected' : 'No motion';
    case 'relay':
      return m.alarmTriggered ? 'Input triggered' : 'Idle';
    default:
      return '—';
  }
}

export function SensorsPage() {
  const { tenantId, base } = useTenantContext();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filters = {
    // Cameras live on their own pages; this list and its counts are sensors only.
    deviceClass: 'sensor' as const,
    siteId: params.get('site') ?? undefined,
    kind: (params.get('kind') as DeviceKind | null) ?? undefined,
    status: (params.get('status') as DeviceStatus | null) ?? undefined,
    q: params.get('q') ?? undefined,
  };
  const devices = useDevices(tenantId, filters);
  const all = useDevices(tenantId, { deviceClass: 'sensor', siteId: filters.siteId });
  const sites = useSites(tenantId);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const counts = (all.data ?? []).reduce((c, d) => ((c[d.status] += 1), c), emptyStatusCounts());
  const siteName = (id: string) => sites.data?.find((s) => s.siteId === id)?.name ?? id;

  const columns: Column<Device>[] = [
    { key: 'status', header: 'Status', sortValue: (d) => d.status, render: (d) => <StatusDot status={d.status} /> },
    { key: 'name', header: 'Name', primary: true, sortValue: (d) => d.name, render: (d) => (
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <KindIcon kind={d.kind} />
        <div className="cell-primary">{d.name}</div>
      </div>
    ) },
    { key: 'kind', header: 'Type', sortValue: (d) => d.kind, render: (d) => KIND_LABELS[d.kind] },
    { key: 'site', header: 'Site', sortValue: (d) => siteName(d.siteId), render: (d) => siteName(d.siteId) },
    { key: 'reading', header: 'Reading', render: (d) => <span className="tabular">{readingSummary(d)}</span> },
    { key: 'battery', header: 'Battery', sortValue: (d) => d.metrics.batteryPercentage, render: (d) => <BatteryMeter percentage={d.metrics.batteryPercentage} low={d.metrics.batteryLow} /> },
    { key: 'signal', header: 'Signal', sortValue: (d) => d.metrics.signalStrength, render: (d) => <span className="small tabular">{formatSignal(d.metrics.signalStrength, d.metrics.signalQuality)}</span> },
    { key: 'seen', header: 'Last seen', sortValue: (d) => d.lastSeenAt ?? '', render: (d) => <TimeAgo iso={d.lastSeenAt} /> },
    { key: 'tags', header: 'Tags', render: (d) => d.tags.map((t) => <span key={t} className="tag">{t}</span>) },
  ];

  if (devices.isError) return <ErrorState error={devices.error} retry={() => devices.refetch()} />;

  return (
    <>
      <PageHeader title="Sensors" subtitle="Sensors reported by this tenant's consoles." />
      <Panel>
        <StatusSummary counts={counts} active={filters.status ?? ''} onSelect={(s) => setParam('status', s)} />
      </Panel>
      <Panel
        flush
        title={<div className="filters">
          <input className="input" placeholder="Search name, MAC, tag" value={filters.q ?? ''} onChange={(e) => setParam('q', e.target.value)} aria-label="Search sensors" />
          <select className="select" value={filters.kind ?? ''} onChange={(e) => setParam('kind', e.target.value)} aria-label="Filter by type">
            <option value="">All types</option>
            {SENSOR_KINDS.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
          </select>
          <select className="select" value={filters.siteId ?? ''} onChange={(e) => setParam('site', e.target.value)} aria-label="Filter by site">
            <option value="">All sites</option>
            {(sites.data ?? []).map((s) => <option key={s.siteId} value={s.siteId}>{s.name}</option>)}
          </select>
          {(filters.q || filters.kind || filters.status || filters.siteId) ? <button type="button" className="btn btn--ghost btn--sm" onClick={() => setParams({}, { replace: true })}>Clear filters</button> : null}
        </div>}
        actions={<span className="small muted">{devices.data?.length ?? 0} sensor{devices.data?.length === 1 ? '' : 's'}</span>}
      >
        {devices.isPending ? (
          <Loading label="Loading sensors…" />
        ) : (
          <DataTable
            columns={columns}
            rows={devices.data}
            rowKey={(d) => d.deviceId}
            onRowClick={(d) => navigate(`${base}/sensors/${encodeURIComponent(d.deviceId)}`)}
            defaultSort={{ key: 'name', dir: 'asc' }}
            refetching={devices.isFetching}
            empty={<div className="empty"><h3>No sensors match</h3><p>Sensors appear after the integration has synced at least once.</p></div>}
          />
        )}
      </Panel>
    </>
  );
}
